// Copyright (c) Move Closer 2022.

import { Component, Inject as VueInject, Mixins, Prop, Vue, Watch } from 'vue-property-decorator'

import { AllowedAttributes } from '../../../contexts'
import {
  defaultProvider,
  Inject,
  IS_MOBILE_PROVIDER_KEY,
  logger,
  QUANTITY_PICKER_CONFIG_PROVIDER_KEY
} from '../../../support'
import { DropdownPlacement, DropdownProps } from '../../../dsl/molecules/Dropdown/Dropdown.contracts'
import { ImageProps } from '../../../dsl/atoms/Image'

import { AuthMixin } from '../../auth/shared'
import { Action, HandleActionOptions, quantityPickerConfigI } from '../../checkout/molecules/CartItem/CartItem.contracts'
import { quantityPickerConfig, sizeClassRegistry } from '../../checkout/molecules/CartItem/CartItem.config'

import { ToastMixin } from './toast.mixin'
import { EventbusType, IEventbus } from '@movecloser/front-core'
import { CART_ID_KEY, CartServiceType, ICartService } from '../../checkout/services/cart'

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
@Component<CartItemMixin>({
  name: 'CartItemMixin'
})
export class CartItemMixin extends Mixins(AuthMixin, ToastMixin) {
  @VueInject({ from: IS_MOBILE_PROVIDER_KEY, default: () => defaultProvider<boolean>(false) })
  public readonly isMobile!: () => boolean

  @Inject(CartServiceType)
  public readonly cartService!: ICartService

  @Prop({ type: String, required: false })
  public readonly categoryUrl?: string

  @VueInject({ from: QUANTITY_PICKER_CONFIG_PROVIDER_KEY, default: () => quantityPickerConfig })
  public readonly quantityPickerConfig!: quantityPickerConfigI

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  @Prop({ required: false, type: Array })
  public actions?: Action[]

  @Prop({ required: true, type: String })
  public description!: string

  @Prop({ required: false, type: String })
  public enhancedDesc?: string

  @Prop({ type: Boolean, required: false, default: false })
  public readonly isCheckout!: boolean

  @Prop({ required: false, type: Boolean, default: false })
  public isDisabled!: boolean

  @Prop({ required: false, type: Boolean, default: false })
  public isGift!: boolean

  @Prop({ required: false, type: Boolean, default: false })
  public isSample!: boolean

  @Prop({ required: false, type: Boolean, default: false })
  public isOutOfStock!: boolean

  @Prop({ required: true, type: Number })
  public price!: number

  @Prop({ required: false, type: Number })
  public promotionPrice: number | undefined

  @Prop({ required: false, type: Number })
  public readonly previousPrice?: number

  @Prop({ required: true, type: Array })
  public properties!: string[]

  @Prop({ required: false, type: Number })
  public quantity?: number

  @Prop({ required: true, type: Object })
  public thumbnail!: ImageProps

  @Prop({ required: true, type: String })
  public title!: string

  @Prop({ required: false, type: Boolean })
  public shouldDisableQuantity?: boolean

  @Prop({ required: false, type: Boolean })
  public shouldLinkCategory?: boolean

  @Prop({ required: false, type: String })
  public size!: string

  @Prop({ type: String, required: false })
  public readonly sku?: string

  @Prop({ required: false, type: Function })
  public quantityCallback!: (change: number) => Promise<boolean> | boolean

  @Prop({ type: String, required: false })
  public readonly uid?: string

  @Prop({ type: String, required: false })
  public readonly urlPath?: string

  public activeActionIndex: number | null = null

  public isAvailable: boolean = true

  public isLoading: boolean = false

  public quantityPickerReload: number = 0

  public get counter (): number {
    return this.quantityPickerConfig.COUNTER_VALUE
  }

  public get dropdown (): DropdownProps {
    return {
      label: this.$t('front.shared.cartItem.more').toString(),
      placement: DropdownPlacement.DownStart,
      container: 'html',
      config: {
        offset: '0'
      },
      items: [
        ...(this.actions ?? []).map(action => {
          return {
            label: action.label,
            type: 'item',
            onClick: action.onClick
          }
        })
      ]
    }
  }

  /**
   * Determines whether actions are present.
   */
  public get hasActions (): boolean {
    return typeof this.actions !== 'undefined' &&
      (Array.isArray(this.actions) && this.actions.length > 0)
  }

  /**
   * Determines whether quantity is passed.
   */
  public get hasQuantity (): boolean {
    return typeof this.quantity !== 'undefined'
  }

  public get isDeleted (): boolean {
    return this.quantity === 0
  }

  public get finalPrice (): number {
    if (typeof this.quantity === 'undefined') {
      return this.price
    }

    return (this.price * this.quantity)
  }

  public get finalPromotionPrice (): number | undefined {
    if (!this.promotionPrice) {
      return
    }

    if (typeof this.quantity === 'undefined') {
      return this.promotionPrice
    }

    return (this.promotionPrice * this.quantity)
  }

  public get maxQty (): number {
    return this.quantityPickerConfig.MAX_VALUE
  }

  public get minQty (): number {
    return this.quantityPickerConfig.MIN_VALUE
  }

  public get sizeClass (): string {
    return sizeClassRegistry[this.size] ?? ''
  }

  public async handleActionClick (
    actionCallback: () => Promise<void> | void,
    activeActionIndex: number,
    options: HandleActionOptions
  ): Promise<void> {
    if (options && (options[AllowedAttributes.IsSample] || options[AllowedAttributes.IsGift])) {
      await this.removeFromCart(options.uid)
      return
    }
    const response = actionCallback()

    this.isLoading = true
    this.activeActionIndex = activeActionIndex

    if (typeof response === 'undefined') {
      this.isLoading = false
      this.activeActionIndex = null

      return
    }

    if (typeof response === 'object' && typeof response.then === 'function') {
      response.then(() => {
        this.activeActionIndex = null
        this.eventBus.emit('cart:item-deleted', options)
      })
        .catch((e: Error) => {
          this.showToast(e.message, 'danger')
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }

  /**
   * Emits new quantity.
   *
   * @param quantity
   */
  public onUpdate (quantity: number): void {
    this.isLoading = true

    /**
     * Hack to update QuantityPicker component.
     * This problem occurs when user put second time bigger value to input
     * than is possible(maxQty).
     *
     * Example (maxQty = 100)
     * User put value 101 -> trigger onBlur -> 100 -> component re-render ->
     * user put value -> 141244 -> trigger onBlur -> 100 -> component is not re-rendering
     */
    this.quantityPickerReload++

    const response: Promise<boolean> | boolean = this.quantityCallback(quantity)

    if (typeof response === 'boolean') {
      this.isAvailable = response
      this.isLoading = false

      return
    }

    if (typeof response === 'object' && typeof response.then === 'function') {
      response.then((isAvailable: boolean) => {
        this.isAvailable = isAvailable
      })
        .catch((e: Error) => {
          this.showToast(e.message, 'danger')
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }

  protected async removeFromCart (uid: string): Promise<void> {
    const cartId = localStorage.getItem(CART_ID_KEY)
    if (!cartId) {
      return
    }

    this.isLoading = true

    try {
      await this.cartService.removeFromCart(cartId, uid)
    } catch (e) {
      logger(e, 'warn')
    } finally {
      this.$emit('updateCart')
      this.eventBus.emit('cart:update-self')
      this.isLoading = false
    }
  }

  /**
   * Notifies parent component with event and extra payload.
   * @param event - event name
   * @param args - arguments that this event should emit
   */
  protected notify (event: string, args: string | object | boolean): void {
    this.$emit(event, args)
  }

  @Watch('isLoading')
  protected onIsLoadingUpdate (value: boolean): void {
    this.notify('isLoading', value)
  }
}
